import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Truck, FileSignature, MapPin, Zap } from 'lucide-react';
import { CellColorPicker } from '@/components/ui/CellColorPicker';
import { CellDateColorPicker } from '@/components/ui/CellDateColorPicker';
import { CellSelectColorPicker } from '@/components/ui/CellSelectColorPicker';
import { CellChassisColorPicker } from '@/components/ui/CellChassisColorPicker';
import { ColoredText } from '@/components/ui/ColoredText';
import PulseOnChange from '@/components/ui/PulseOnChange';

/**
 * ShipmentTableCell - Renders individual cells for the shipment table
 * Extracted from ShipmentDashboard.js to reduce complexity
 */

// Compact date formatter: returns MM/DD only (no year) to save dashboard space
const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  try {
    let m, d;
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      m = parseInt(dateStr.substring(5, 7));
      d = parseInt(dateStr.substring(8, 10));
    } else if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const parts = dateStr.split('/');
      m = parseInt(parts[0]);
      d = parseInt(parts[1]);
    } else {
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) return '-';
      m = dt.getMonth() + 1;
      d = dt.getDate();
    }
    return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}`;
  } catch { return '-'; }
};

// Helper components for specific cell types
const MarkerCell = ({ lastEditedShipmentId, shipmentId }) => {
  if (lastEditedShipmentId === shipmentId) {
    return (
      <div 
        className="w-1.5 h-6 bg-green-500 rounded-sm animate-blink" 
        title="Last edited"
      />
    );
  }
  return <div className="w-1.5 h-6" />;
};

const ImportantCell = ({ isImportant }) => {
  if (!isImportant) return null;
  return (
    <span 
      className="w-2.5 h-2.5 bg-purple-500 rounded-full inline-block animate-blink"
      title="Important shipment"
    />
  );
};

const ActionsCell = ({ 
  shipment, 
  isCustomer, 
  onDispatch, 
  onEdit, 
  onLocationChange, 
  onTrackNow,
  trackingBusy,
  t 
}) => {
  if (isCustomer) {
    return <span className="text-slate-500 text-xs">View only</span>;
  }
  const isTracking = trackingBusy === shipment.id;
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
        onClick={() => onDispatch(shipment)}
        title={t('tooltips.sendDispatch')}
        data-testid={`dispatch-btn-${shipment.id}`}
      >
        <Truck className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
        onClick={() => onEdit(shipment)}
        title={t('tooltips.editLoad')}
        data-testid={`edit-btn-${shipment.id}`}
      >
        <FileSignature className="h-3.5 w-3.5" />
      </Button>
      {/* iter 294.5 — Track Now lightning-bolt. One-click re-check of the
          terminal (eModal / WBCT / APM / …) for this shipment. Yellow when
          a terminal_id is set, muted otherwise. Prevents dispatchers from
          opening Edit → Terminal Availability → Check just to refresh LFD
          after moving a container to a new terminal. */}
      {onTrackNow && shipment.terminal_id && (
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${isTracking ? 'text-amber-300 animate-pulse' : 'text-amber-400 hover:text-amber-300 hover:bg-slate-800'}`}
          onClick={() => onTrackNow(shipment)}
          title={isTracking ? 'Checking terminal…' : 'Track now (re-check terminal for LFD, holds, availability)'}
          data-testid={`track-now-btn-${shipment.id}`}
          disabled={isTracking}
        >
          <Zap className="h-3.5 w-3.5" fill={isTracking ? 'currentColor' : 'none'} />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
        onClick={() => onLocationChange(shipment)}
        title="Change Location / Status"
        data-testid={`change-location-btn-${shipment.id}`}
      >
        <MapPin className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

const StatusCell = ({ status, statusColors }) => (
  <Badge 
    variant="outline" 
    className={`${statusColors[status]} border text-xs px-1.5 py-0.5`}
  >
    {status}
  </Badge>
);

const PPCTFCell = ({ ppPaid, ctfPaid, ppPaidBy, ctfPaidBy }) => (
  <div className="flex items-center gap-1">
    {ppPaid && (
      <span className="w-2 h-2 bg-red-500 rounded-full" title={`PP Paid by ${ppPaidBy || 'N/A'}`} />
    )}
    {ctfPaid && (
      <span className="w-2 h-2 bg-red-500 rounded-full" title={`CTF Paid by ${ctfPaidBy || 'N/A'}`} />
    )}
    {!ppPaid && !ctfPaid && <span className="text-slate-500 text-xs">-</span>}
  </div>
);

const OverweightCell = ({ isOverweight }) => {
  if (isOverweight) {
    return (
      <Badge className="bg-red-500/20 text-red-400 border border-red-500/50 text-[10px] px-1.5 py-0.5 font-bold" title="Overweight Container">
        OW
      </Badge>
    );
  }
  return <span className="text-slate-600 text-xs">-</span>;
};

const HazmatCell = ({ isHazmat }) => {
  if (isHazmat) {
    return (
      <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/50 text-[10px] px-1.5 py-0.5 font-bold" title="Hazmat Container" data-testid="hazmat-badge">
        HZ
      </Badge>
    );
  }
  return <span className="text-slate-600 text-xs">-</span>;
};

const DocsCell = ({ docCount }) => {
  if (docCount > 0) {
    return <span className="text-blue-400 text-xs font-medium">{docCount}</span>;
  }
  return <span className="text-slate-500 text-xs">-</span>;
};

/**
 * Main ShipmentTableCell Component
 */
export const ShipmentTableCell = ({
  columnId,
  shipment,
  // State & handlers
  lastEditedShipmentId,
  isCustomer,
  canEdit,
  isDark,
  // Event handlers
  onDispatch,
  onEdit,
  onLocationChange,
  onTrackNow,
  trackingBusy,
  onCellColorChange,
  onInlineDateSave,
  onAppointmentTimeSave,
  onDeliveryTimeSave,
  onPickupTimeSave,
  onMtyReturnTimeSave,
  onCommentsClick,
  onTimelineClick,
  onChassisSave,
  // Inline edit state
  inlineEditCell,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  handleInlineEditSave,
  cancelInlineEdit,
  // Master data & helpers
  masterData,
  getMasterName,
  getMasterCode,
  formatDate,
  formatTime,
  shouldLFDBlink,
  getDaysUntilLFD,
  statusColors,
  t
}) => {
  switch (columnId) {
    case 'marker':
      return <MarkerCell lastEditedShipmentId={lastEditedShipmentId} shipmentId={shipment.id} />;
    
    case 'important':
      return <ImportantCell isImportant={shipment.is_important} />;
    
    case 'actions':
      return (
        <ActionsCell 
          shipment={shipment}
          isCustomer={isCustomer}
          onDispatch={onDispatch}
          onEdit={onEdit}
          onLocationChange={onLocationChange}
          onTrackNow={onTrackNow}
          trackingBusy={trackingBusy}
          t={t}
        />
      );
    
    case 'container_number':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.container_number}
          onColorChange={(color) => onCellColorChange(shipment.id, 'container_number', color)}
          onCommentsClick={onCommentsClick ? () => onCommentsClick(shipment) : null}
          commentCount={shipment.comment_count || 0}
          onTimelineClick={onTimelineClick ? () => onTimelineClick(shipment) : null}
        >
          <span className="relative inline-flex items-center">
            <ColoredText 
              color={shipment.cell_colors?.container_number}
              className="font-mono text-xs px-1 rounded"
              title="Right-click for color and comments"
            >
              {shipment.container_number || '-'}
            </ColoredText>
            {shipment.comment_count > 0 && (
              <span
                // Larger dot with white ring so it pops against any status-
                // coloured row in BOTH light and dark mode. The ring acts as
                // an outline; the inner sky-300 fill stays readable on amber/
                // green/dark backgrounds where the old blue-400 vanished.
                className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-sky-400 ring-1 ring-white/90 shadow-[0_0_4px_rgba(56,189,248,0.9)] dark:ring-slate-900/60 dark:bg-sky-300"
                title={`${shipment.comment_count} comment${shipment.comment_count === 1 ? '' : 's'}`}
                data-testid={`comment-dot-${shipment.id}`}
                aria-label={`${shipment.comment_count} comments`}
              />
            )}
          </span>
        </CellColorPicker>
      );
    
    case 'container_size':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.container_size}
          onColorChange={(color) => onCellColorChange(shipment.id, 'container_size', color)}
        >
          {inlineEditCell?.rowId === shipment.id && inlineEditCell?.field === 'container_size' ? (
            <Input
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              onBlur={handleInlineEditSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInlineEditSave();
                if (e.key === 'Escape') cancelInlineEdit();
              }}
              autoFocus
              className="h-6 px-1 text-xs bg-slate-800 border-blue-500 text-white w-16"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <ColoredText 
              color={shipment.cell_colors?.container_size}
              className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded"
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) startInlineEdit(shipment.id, 'container_size', shipment.container_size);
              }}
              title={canEdit ? "Click to edit, Right-click for color" : ""}
            >
              {shipment.container_size || '-'}
            </ColoredText>
          )}
        </CellColorPicker>
      );
    
    case 'ssl':
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.ssl}
          currentValue={shipment.ssl_id}
          options={masterData.ssls || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'ssl', color)}
          onValueChange={(value) => onInlineDateSave(shipment.id, 'ssl_id', value)}
          fieldLabel="SSL (Shipping Line)"
          placeholder="Select SSL..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={shipment.cell_colors?.ssl}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
            title={getMasterName('ssls', shipment.ssl_id)}
          >
            {getMasterCode('ssls', shipment.ssl_id)}
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'chassis':
      return (
        <CellChassisColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.chassis}
          currentChassisType={shipment.chassis_type}
          currentChassisNumber={shipment.chassis_number}
          onColorChange={(color) => onCellColorChange(shipment.id, 'chassis', color)}
          onChassisChange={(type, number) => onChassisSave && onChassisSave(shipment.id, type, number)}
          chassisTypes={masterData.chassis_types}
        >
          <ColoredText 
            color={shipment.cell_colors?.chassis}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded leading-tight"
            title="Click to edit chassis"
          >
            {shipment.chassis_type && shipment.chassis_number 
              ? <><div>{shipment.chassis_type}</div><div className="font-mono">{shipment.chassis_number}</div></>
              : shipment.chassis_number || shipment.chassis_type || '-'}
          </ColoredText>
        </CellChassisColorPicker>
      );
    
    case 'job_number': {
      const displayJobNumber = shipment.job_number?.replace(/^JOB/i, '') || '-';
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.job_number}
          onColorChange={(color) => onCellColorChange(shipment.id, 'job_number', color)}
        >
          <ColoredText 
            color={shipment.cell_colors?.job_number}
            className="font-mono text-xs cursor-pointer hover:underline"
            title={shipment.job_number}
          >
            {displayJobNumber}
          </ColoredText>
        </CellColorPicker>
      );
    }
    
    case 'status':
      return <StatusCell status={shipment.status} statusColors={statusColors} />;
    
    case 'eta':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.eta}
          onColorChange={(color) => onCellColorChange(shipment.id, 'eta', color)}
        >
          <ColoredText color={shipment.cell_colors?.eta} className="text-xs">
            {formatDateShort(shipment.eta)}
          </ColoredText>
        </CellColorPicker>
      );
    
    case 'pp_ctf':
      return (
        <PPCTFCell 
          ppPaid={shipment.pier_pass_paid}
          ctfPaid={shipment.ctf_paid}
          ppPaidBy={shipment.pier_pass_paid_by}
          ctfPaidBy={shipment.ctf_paid_by}
        />
      );
    
    case 'empty_return':
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.empty_return}
          currentValue={shipment.empty_return_location_id}
          options={masterData.terminals || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'empty_return', color)}
          onValueChange={(value) => onInlineDateSave(shipment.id, 'empty_return_location_id', value)}
          fieldLabel="Empty Return Terminal"
          placeholder="Select terminal..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={shipment.cell_colors?.empty_return}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
            title={getMasterName('terminals', shipment.empty_return_location_id) || 'Click to select terminal'}
          >
            {getMasterCode('terminals', shipment.empty_return_location_id)}
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'docs':
      return <DocsCell docCount={shipment.documents?.length || 0} />;
    
    case 'appointment':
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.appointment}
          currentDate={shipment.appointment_date}
          currentTimeFrom={shipment.appointment_time_from}
          currentTimeTo={shipment.appointment_time_to}
          onColorChange={(color) => onCellColorChange(shipment.id, 'appointment', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'appointment_date', date)}
          onTimeChange={(timeFrom, timeTo) => onAppointmentTimeSave && onAppointmentTimeSave(shipment.id, timeFrom, timeTo)}
          fieldLabel="Appointment Date"
          showTimePicker={true}
        >
          <ColoredText 
            color={shipment.cell_colors?.appointment}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded min-w-[50px] inline-block"
            title="Click to edit appointment"
          >
            {formatDateShort(shipment.appointment_date) || '-'}
            {shipment.appointment_time_from && (
              <ColoredText color={shipment.cell_colors?.appointment} className="block text-[10px]">
                {formatTime(shipment.appointment_time_from, shipment.appointment_time_to)}
              </ColoredText>
            )}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'overweight':
      return <OverweightCell isOverweight={shipment.is_overweight} />;
    
    case 'hazmat':
      return <HazmatCell isHazmat={shipment.is_hazmat} />;
    
    case 'delivery_date': {
      const isDeliveryToday = shipment.delivery_date && 
        new Date(shipment.delivery_date).toDateString() === new Date().toDateString();
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.delivery_date}
          currentDate={shipment.delivery_date}
          currentTimeFrom={shipment.delivery_time_from}
          currentTimeTo={shipment.delivery_time_to}
          onColorChange={(color) => onCellColorChange(shipment.id, 'delivery_date', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'delivery_date', date)}
          onTimeChange={(timeFrom, timeTo) => onDeliveryTimeSave && onDeliveryTimeSave(shipment.id, timeFrom, timeTo)}
          fieldLabel="Delivery Date"
          showTimePicker={true}
        >
          <span 
            className={`text-xs font-medium px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block text-center ${isDeliveryToday ? 'delivery-today' : ''}`}
            style={{ color: shipment.cell_colors?.delivery_date || (isDeliveryToday ? '#22c55e' : '#cbd5e1') }}
            title="Click to edit date & time"
          >
            {formatDateShort(shipment.delivery_date) || '-'}
            {shipment.delivery_time_from && (
              <span className="block text-[10px]" style={{ color: shipment.cell_colors?.delivery_date || '#94a3b8' }}>
                {formatTime(shipment.delivery_time_from, shipment.delivery_time_to)}
              </span>
            )}
          </span>
        </CellDateColorPicker>
      );
    }
    
    case 'lfd': {
      const lfdBlink = shouldLFDBlink(shipment.lfd);
      const daysLeft = getDaysUntilLFD(shipment.lfd);
      const isManualLFD = shipment.lfd_manual_override;
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.lfd}
          currentDate={shipment.lfd}
          onColorChange={(color) => onCellColorChange(shipment.id, 'lfd', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'lfd', date)}
          fieldLabel="Last Free Day (LFD)"
        >
          <span 
            className={`text-xs font-medium px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block text-center ${lfdBlink ? 'lfd-urgent' : ''}`}
            style={{ 
              color: shipment.cell_colors?.lfd || (
                lfdBlink ? undefined : 
                daysLeft !== null && daysLeft <= 3 ? '#fb923c' : '#cbd5e1'
              )
            }}
            title={`${daysLeft !== null ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} until LFD` : 'Click to set LFD'}${isManualLFD ? ' (Manual)' : ''}`}
          >
            {formatDateShort(shipment.lfd)}
            {isManualLFD && <span className="ml-0.5 text-[9px] text-blue-400" title="Manually set - won't be overwritten by tracking">M</span>}
            {lfdBlink && daysLeft !== null && (
              <span className="ml-1 text-[10px] font-bold">
                {daysLeft === 0 ? '(TODAY!)' : daysLeft < 0 ? `(${Math.abs(daysLeft)}d over)` : `(${daysLeft}d)`}
              </span>
            )}
          </span>
        </CellDateColorPicker>
      );
    }
    
    case 'mty_rdy':
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.mty_rdy}
          currentDate={shipment.mty_rdy}
          onColorChange={(color) => onCellColorChange(shipment.id, 'mty_rdy', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'mty_rdy', date)}
          fieldLabel="MTY Ready Date"
        >
          <ColoredText 
            color={shipment.cell_colors?.mty_rdy}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block text-center"
            title="Click to edit date"
          >
            {formatDateShort(shipment.mty_rdy) || '-'}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'mty_return':
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.mty_return}
          currentDate={shipment.mty_return}
          currentTimeFrom={shipment.mty_return_time_from}
          currentTimeTo={shipment.mty_return_time_to}
          onColorChange={(color) => onCellColorChange(shipment.id, 'mty_return', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'mty_return', date)}
          onTimeChange={(timeFrom, timeTo) => onMtyReturnTimeSave && onMtyReturnTimeSave(shipment.id, timeFrom, timeTo)}
          fieldLabel="MTY Return Date"
          showTimePicker={true}
        >
          <ColoredText 
            color={shipment.cell_colors?.mty_return}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block text-center"
            title="Click to edit date & time"
          >
            {formatDateShort(shipment.mty_return) || '-'}
            {shipment.mty_return_time_from && (
              <span className="block text-[10px]" style={{ color: shipment.cell_colors?.mty_return || '#94a3b8' }}>
                {formatTime(shipment.mty_return_time_from, shipment.mty_return_time_to)}
              </span>
            )}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'return_location':
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.return_location}
          currentValue={shipment.return_location}
          options={masterData.terminals || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'return_location', color)}
          onValueChange={(value) => onInlineDateSave(shipment.id, 'return_location', value)}
          fieldLabel="Return Terminal (RT)"
          placeholder="Select terminal..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={shipment.cell_colors?.return_location}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
            title={getMasterName('terminals', shipment.return_location) || 'Click to select return terminal'}
          >
            {getMasterCode('terminals', shipment.return_location) || '-'}
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'yard': {
      const currentYard = masterData.yards?.find(y => y.id === shipment.yard_id);
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.yard}
          currentValue={shipment.yard_id}
          options={masterData.yards || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'yard', color)}
          onValueChange={(value) => onInlineDateSave(shipment.id, 'yard_id', value)}
          fieldLabel="Yard"
          placeholder="Select yard..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={shipment.cell_colors?.yard}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block"
            title="Click to select yard"
          >
            {currentYard?.name || '-'}
          </ColoredText>
        </CellSelectColorPicker>
      );
    }
    
    case 'customer':
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.customer}
          currentValue={shipment.customer_id}
          options={masterData.customers || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'customer', color)}
          onValueChange={(value) => {
            const customer = masterData.customers?.find(c => c.id === value);
            onInlineDateSave(shipment.id, 'customer_id', value);
            if (customer) {
              onInlineDateSave(shipment.id, 'customer_name', customer.name);
            }
          }}
          fieldLabel="Customer"
          placeholder="Select customer..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={shipment.cell_colors?.customer}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block"
            title="Click to select customer"
          >
            <PulseOnChange value={shipment.customer_name || ''}>
              {shipment.customer_name || '-'}
            </PulseOnChange>
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'deliver_to': {
      const addressParts = [shipment.delivery_address, shipment.delivery_city, shipment.delivery_state].filter(Boolean);
      const fullAddress = addressParts.join(', ') || '-';
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.deliver_to}
          onColorChange={(color) => onCellColorChange(shipment.id, 'deliver_to', color)}
        >
          {inlineEditCell?.rowId === shipment.id && inlineEditCell?.field === 'delivery_address' ? (
            <Input
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              onBlur={handleInlineEditSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInlineEditSave();
                if (e.key === 'Escape') cancelInlineEdit();
              }}
              autoFocus
              className="h-6 px-1 text-xs bg-slate-800 border-blue-500 text-white w-36"
              onClick={(e) => e.stopPropagation()}
              placeholder="Address"
            />
          ) : (
            <ColoredText 
              color={shipment.cell_colors?.deliver_to}
              className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 inline-block whitespace-normal break-words leading-tight" 
              title={fullAddress !== '-' ? fullAddress : "Click to add address"}
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) startInlineEdit(shipment.id, 'delivery_address', shipment.delivery_address);
              }}
            >
              {fullAddress}
            </ColoredText>
          )}
        </CellColorPicker>
      );
    }
    
    case 'driver':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.driver}
          onColorChange={(color) => onCellColorChange(shipment.id, 'driver', color)}
        >
          <ColoredText 
            color={shipment.cell_colors?.driver}
            className="text-xs cursor-pointer hover:underline"
          >
            {shipment.driver_name || '-'}
          </ColoredText>
        </CellColorPicker>
      );
    
    case 'remarks':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.remarks}
          onColorChange={(color) => onCellColorChange(shipment.id, 'remarks', color)}
        >
          {inlineEditCell?.rowId === shipment.id && inlineEditCell?.field === 'remarks' ? (
            <textarea
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              onBlur={handleInlineEditSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleInlineEditSave();
                }
                if (e.key === 'Escape') cancelInlineEdit();
              }}
              autoFocus
              rows={2}
              className="px-1 py-0.5 text-xs bg-slate-800 border border-blue-500 text-white w-full min-w-[120px] rounded resize-none"
              onClick={(e) => e.stopPropagation()}
              placeholder="Enter remarks..."
            />
          ) : (
            <div 
              className="w-full h-full min-h-[28px] cursor-pointer hover:bg-slate-700/50 rounded flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) startInlineEdit(shipment.id, 'remarks', shipment.remarks);
              }}
              title={shipment.remarks || (canEdit ? "Click to edit, Right-click for color" : "")}
            >
              <span 
                className="text-xs px-1 block w-full"
                style={{ 
                  color: shipment.cell_colors?.remarks || (isDark ? '#94a3b8' : '#1e293b'),
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: '1.3',
                  maxHeight: '2.6em'
                }}
              >
                {shipment.remarks || '-'}
              </span>
            </div>
          )}
        </CellColorPicker>
      );
    
    case 'terminal':
      return (
        <CellSelectColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.terminal}
          currentValue={shipment.terminal_id}
          options={masterData.terminals || []}
          onColorChange={(color) => onCellColorChange(shipment.id, 'terminal', color)}
          onValueChange={(value) => {
            const terminal = masterData.terminals?.find(t => t.id === value);
            onInlineDateSave(shipment.id, 'terminal_id', value);
            if (terminal) {
              onInlineDateSave(shipment.id, 'terminal_name', terminal.name);
            }
          }}
          fieldLabel="Terminal"
          placeholder="Select terminal..."
          valueKey="id"
          labelKey="name"
        >
          {(!shipment.terminal_id) ? (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/40 cursor-pointer hover:bg-amber-500/25 inline-flex items-center gap-1 whitespace-nowrap"
              title="No terminal — this container won't be picked up by the 4×/day availability scheduler. Click to assign one."
              data-testid={`no-terminal-badge-${shipment.id}`}
            >
              <span className="text-[8px] leading-none">⚠</span>
              No terminal
            </span>
          ) : (
            <ColoredText 
              color={shipment.cell_colors?.terminal}
              className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
              title={shipment.terminal_name || getMasterName('terminals', shipment.terminal_id)}
            >
              {getMasterCode('terminals', shipment.terminal_id)}
              {shipment.terminal_auto_assigned ? (
                <span
                  className="ml-1 text-[8px] leading-none align-middle text-cyan-300"
                  title={`Auto-assigned (${shipment.terminal_auto_source || 'history'}) — click to override`}
                >★</span>
              ) : null}
            </ColoredText>
          )}
        </CellSelectColorPicker>
      );
    
    case 'mbl':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.mbl}
          onColorChange={(color) => onCellColorChange(shipment.id, 'mbl', color)}
        >
          <ColoredText color={shipment.cell_colors?.mbl} className="text-xs">
            {shipment.mbl || shipment.master_bl || '-'}
          </ColoredText>
        </CellColorPicker>
      );
    
    case 'pin':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.pin}
          onColorChange={(color) => onCellColorChange(shipment.id, 'pin', color)}
        >
          <ColoredText color={shipment.cell_colors?.pin} className="text-xs">
            {shipment.pin_number || shipment.pin || '-'}
          </ColoredText>
        </CellColorPicker>
      );
    
    case 'customs_status':
      return (
        <CellColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.customs_status}
          onColorChange={(color) => onCellColorChange(shipment.id, 'customs_status', color)}
        >
          <ColoredText 
            color={shipment.cell_colors?.customs_status || (
              shipment.customs_status === 'cleared' ? '#4ade80' :
              shipment.customs_status === 'hold' ? '#f87171' : null
            )} 
            className="text-xs"
          >
            {shipment.customs_status || '-'}
          </ColoredText>
        </CellColorPicker>
      );
    
    case 'pickup_date':
      return (
        <CellDateColorPicker 
          containerNumber={shipment.container_number}
          currentColor={shipment.cell_colors?.pickup_date}
          currentDate={shipment.pickup_date}
          currentTimeFrom={shipment.pickup_time_from}
          currentTimeTo={shipment.pickup_time_to}
          onColorChange={(color) => onCellColorChange(shipment.id, 'pickup_date', color)}
          onDateChange={(date) => onInlineDateSave(shipment.id, 'pickup_date', date)}
          onTimeChange={(timeFrom, timeTo) => onPickupTimeSave && onPickupTimeSave(shipment.id, timeFrom, timeTo)}
          fieldLabel="Pickup Date"
          showTimePicker={true}
        >
          <ColoredText 
            color={shipment.cell_colors?.pickup_date} 
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block text-center"
            title="Click to edit date & time"
          >
            {formatDateShort(shipment.pickup_date) || '-'}
            {shipment.pickup_time_from && (
              <span className="block text-[10px]" style={{ color: shipment.cell_colors?.pickup_date || '#94a3b8' }}>
                {formatTime(shipment.pickup_time_from, shipment.pickup_time_to)}
              </span>
            )}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    default:
      return '-';
  }
};

export default ShipmentTableCell;
